/* eslint react/jsx-key: 0 */
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import {
  ROW_TYPE_FLEX_SERIES,
  ROW_TYPE_MULTI_STORY,
  ROW_TYPE_ONE_FEATURED_STORY,
  ROW_TYPE_ONE_STORY_HERO,
  ROW_TYPE_ONE_PRIME_CARD,
  ROW_TYPE_THREE_CHANNELS,
  ROW_TYPE_THREE_PREMIUM_CARDS,
  ROW_TYPE_THREE_PRIME_CARDS,
  ROW_TYPE_THREE_STANDARD_CARDS,
  ROW_TYPE_THREE_FEATURED_CARDS,
  ROW_TYPE_TWO_STANDARD_CARDS,
  ROW_TYPE_FOUR_VIDEO,
  ROW_TYPE_FLEX_VIDEO,
  ROW_TYPE_SHOPPING_HERO,
  ROW_TYPE_FEATURED_CATEGORY,
  ROW_TYPE_FLEX_CATEGORY,
  ROW_TYPE_TWO_CATEGORY,
  ROW_TYPE_MOST_POPULAR
} from '../../constants';
import MediaQuery, { MEDIA_QUERIES } from './media_query';
import Lazy from './lazy';
import ResponsiveImg from './responsive_img';
import { openerImageTypeToRatio } from '../../utils/images';
import classNames from 'classnames';
import { compose } from 'redux';
import {get, find, each, values} from 'lodash';
import { captureMessage } from '../../utils/browserSentry';

const withHeights = (imageSizes) => {
  const imageSizesWithHeights = {};

  each(imageSizes, (sizes, cardType) => {
    imageSizesWithHeights[cardType] = sizes.map(size => {
      const { type, width } = size;
      const ratio = openerImageTypeToRatio(type);
      return {
        type,
        width,
        height: width ? width / ratio : null
      };
    });
  });
  return imageSizesWithHeights;
};

/*
 * Some things to know about this config:
 *
 * - First element is desktop, second tablet, third mobile.
 *
 * - The `height` is calculated based on the `width` and the ratio,
 *   and these dimensions determine how large of a jpg/png to request.
 *
 * - We always want the image to be properly sized to its container.
 *   The <ResponsiveImg> component is responsible for this.
 *
 * - Some images are full-bleed on one or more breakpoints. In this case,
 *   we want the image size to be the largest that could possibly
 *   be displayed on that breakpoint.
 *
 *   That should explain the entries that look like:
 *     { type: '3x4', width: MEDIA_QUERIES.mobile.maxWidth }
 *
 * - This is tested in spec/feature/images_sizes.feature, which
 *   leverages the test helper JS functions in
 *   js/initializers/featureTestHelpers.js.
 *
 *   You can run a quicker test by using `gulp dev`, navigating to a page
 *   with the `?debug_responsive_images=true` param, then invoking
 *   `window.countResizeImages();`.
 */

const IMAGE_SIZES = withHeights({
  [ROW_TYPE_MULTI_STORY]: [
    { type: '16x9', width: 764 },
    { type: '5x6', width: 371 },
    { type: '16x9', width: 415 }
  ],
  [ROW_TYPE_ONE_STORY_HERO]: [
    { type: '8x3', width: 1800 },
    { type: '4x3', width: MEDIA_QUERIES.tablet.maxWidth },
    { type: '3x4', width: MEDIA_QUERIES.mobile.maxWidth }
  ],
  [ROW_TYPE_ONE_FEATURED_STORY]: [
    { type: '8x3', width: 1800 },
    { type: '4x3', width: MEDIA_QUERIES.tablet.maxWidth },
    { type: '3x4', width: MEDIA_QUERIES.mobile.maxWidth }
  ],
  [ROW_TYPE_THREE_CHANNELS]: [
    { type: '8x3', width: 340 },
    { type: '8x3', width: 340 },
    { type: '8x3', width: 340 }
  ],
  [ROW_TYPE_THREE_PREMIUM_CARDS]: [
    { type: '16x9', width: 340 },
    { type: '16x9', width: 215 },
    { type: '8x3', width: MEDIA_QUERIES.mobile.maxWidth }
  ],
  [ROW_TYPE_FLEX_SERIES]: [
    { type: '5x6', width: 245 },
    { type: '5x6', width: 225 },
    { type: '5x6', width: 235 }
  ],
  [ROW_TYPE_THREE_PRIME_CARDS]: [
    { type: '5x6', width: 340 },
    { type: '8x3', width: 728 },
    { type: '5x6', width: MEDIA_QUERIES.mobile.maxWidth }
  ],
  [ROW_TYPE_ONE_PRIME_CARD]: [
    { type: '5x6', width: 340 },
    { type: '8x3', width: 728 },
    { type: '5x6', width: MEDIA_QUERIES.mobile.maxWidth }
  ],
  [ROW_TYPE_THREE_STANDARD_CARDS]: [
    { type: '5x6', width: 140 },
    { type: '5x6', width: 140 },
    { type: '5x6', width: 140 }
  ],
  [ROW_TYPE_TWO_STANDARD_CARDS]: [
    { type: '5x6', width: 140 },
    { type: '5x6', width: 140 },
    { type: '5x6', width: 140 }
  ],
  [`first-${ROW_TYPE_THREE_FEATURED_CARDS}`]: [
    { type: '8x3', width: 1000 },
    { type: '8x3', width: MEDIA_QUERIES.tablet.maxWidth },
    { type: '5x6', width: MEDIA_QUERIES.mobile.maxWidth }
  ],
  [ROW_TYPE_THREE_FEATURED_CARDS]: [
    { type: '4x3', width: 500 },
    { type: '4x3', width: 610 },
    { type: '5x6', width: MEDIA_QUERIES.mobile.maxWidth }
  ],
  [ROW_TYPE_FOUR_VIDEO]: [
    { type: '16x9', width: 245 },
    { type: '16x9', width: 340 },
    { type: '16x9', width: MEDIA_QUERIES.mobile.maxWidth }
  ],
  [ROW_TYPE_FLEX_VIDEO]: [
    { type: '16x9', width: 245 },
    { type: '16x9', width: 340 },
    { type: '16x9', width: MEDIA_QUERIES.mobile.maxWidth }
  ],
  [ROW_TYPE_SHOPPING_HERO]: [
    { type: '8x3', width: 1800 },
    { type: '8x3', width: MEDIA_QUERIES.tablet.maxWidth },
    { type: '4x3', width: MEDIA_QUERIES.mobile.maxWidth }
  ],
  [ROW_TYPE_FEATURED_CATEGORY]: [
    { type: '8x3', width: 815 },
    { type: '8x3', width: 760 },
    { type: '4x3', width: MEDIA_QUERIES.mobile.maxWidth }
  ],
  [ROW_TYPE_FLEX_CATEGORY]: [
    { type: '16x9', width: 245 },
    { type: '16x9', width: 340 },
    { type: '16x9', width: 245 }
  ],
  [ROW_TYPE_TWO_CATEGORY]: [
    { type: '8x3', width: 530 },
    { type: '8x3', width: 350 },
    { type: '4x3', width: MEDIA_QUERIES.mobile.maxWidth }
  ],
  [ROW_TYPE_MOST_POPULAR]: [
    { type: '5x6', width: 140 },
    { type: '5x6', width: 140 },
    { type: '5x6', width: 140 }
  ],
  ['related-entries']: [
    { type: '16x9', width: 200 },
    { type: '5x6', width: 140 },
    { type: '5x6', width: 140 }
  ],
  'fallback': [
    { type: '5x6', width: null },
    { type: '5x6', width: null },
    { type: '4x3', width: null }
  ]
});

const getImageSizesKey = ({ rowType }) => {
  const recognizedRowTypes = Object.keys(IMAGE_SIZES);

  if (recognizedRowTypes.includes(rowType)) {
    return rowType;
  }

  captureMessage(
    'Tried to render a card with an unrecognized row type.',
    { extra: { rowType } }
  );
  return 'fallback';
};

const getImageSizes = (props) => ({
  ...props,
  sizes: IMAGE_SIZES[getImageSizesKey(props)]
});

const OpenerImage = compose(
  ({ sizes: [desktop, tablet, mobile], ...props }) => (
    <div>
      <MediaQuery breakpoint="desktop">
        <OpenerImageNode {...props} sizes={desktop} />
      </MediaQuery>
      <MediaQuery breakpoint="tablet">
        <OpenerImageNode {...props} sizes={tablet} />
      </MediaQuery>
      <MediaQuery breakpoint="mobile">
        <OpenerImageNode {...props} sizes={mobile} />
      </MediaQuery>
    </div>
  ),
  getImageSizes
);

export default OpenerImage;

OpenerImage.propTypes = {
  rowType: PropTypes.string.isRequired,
  opener_images: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string,
    alt_title: PropTypes.string,
    asset: PropTypes.shape({
      src: PropTypes.object
    })
  })).isRequired,
  isLastInRow: PropTypes.bool
};

OpenerImage.defaultProps = {
  rowType: '',
  opener_images: [{
    type: '',
    asset: { src: {} }
  }]
};

export class OpenerImageNode extends PureComponent {
  state = {
    isLoading: true,
    isErrored: false
  };

  handleError = () => this.setState({ isErrored: true });

  handleLoad = () => this.setState({ isLoading: false });

  getImgData() {
    const {
      opener_images,
      sizes: { type }
    } = this.props;
    const openerImagesForType = find(opener_images, { type });
    return {
      alt_title: get(openerImagesForType, 'alt_title'),
      src: values(get(openerImagesForType, 'asset.src', {}))[0]
    };

    /*
     * Note: with our new ResponsiveImg component, it doesn't matter which
     * src we pull from the Bridge response -- the resizing will happen on
     * the frontend. The following opener image objects are all valid:
     *
     * - { asset: { src: { full: 'image.jpg' } } }
     * - { asset: { src: { '760x430': 'image.jpg' } }
     * - { asset: { src: { full: 'image1.jpg', '760x430': 'image2.jpg' } } }
     *
     * TODO:
     * - have Foundryv2 and Bridge only return src.full, like in the first
     *   example
     */
  }

  render() {
    const {
      sizes: { width, height, lastInRowWidth, lastInRowHeight },
      isLastInRow,
      isVideoCard,
      rowType
    } = this.props;
    const imgData = this.getImgData();
    const shouldDisplayImage = !this.state.isErrored && imgData.src;
    const className = classNames('opener-image', {
      error: !shouldDisplayImage,
      'video': isVideoCard && rowType !== ROW_TYPE_ONE_STORY_HERO
    });

    return (
      <Lazy component="div" className={className}>
        {
          shouldDisplayImage
            ? (
              <ResponsiveImg
                src={imgData.src}
                alt={imgData.alt_title}
                width={isLastInRow ? lastInRowWidth || width : width}
                height={isLastInRow ? lastInRowHeight || height : height}
                onError={this.handleError}
                onLoad={this.handleLoad}
                className={classNames({
                  'series-image': rowType === ROW_TYPE_FLEX_SERIES,
                  'channel-image': rowType === ROW_TYPE_THREE_CHANNELS,
                  'category-image': [ROW_TYPE_FEATURED_CATEGORY, ROW_TYPE_FLEX_CATEGORY].includes(rowType),
                  'featured-stories-image': rowType === ROW_TYPE_THREE_FEATURED_CARDS,
                  'first-featured-stories-image': rowType === `first-${ROW_TYPE_THREE_FEATURED_CARDS}`,
                  'loading': this.state.isLoading
                })}
              />
            )
            : <span />
        }
      </Lazy>
    );
  }
}

OpenerImageNode.propTypes = {
  opener_images: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string,
    alt_title: PropTypes.string,
    asset: PropTypes.shape({
      src: PropTypes.object
    })
  })).isRequired,
  sizes: PropTypes.shape({
    type: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    lastInRowWidth: PropTypes.number,
    lastInRowHeight: PropTypes.number
  }).isRequired,
  rowType: PropTypes.string,
  isLastInRow: PropTypes.bool,
  isVideoCard: PropTypes.bool,
  alt: PropTypes.string
};
